import { Either, isRight } from 'fp-ts/lib/Either'
import { useCallback } from 'react'
import { QueryFunction, QueryFunctionContext, QueryKey, useQuery, UseQueryOptions } from 'react-query'
import { ErrorType } from '../../types/error'
import { omit } from '../../utils/functions/omit'
import { DecodedHttpResponse } from '../useFetch'

type UseQueryEitherOptions<QK, E extends ErrorType, P extends DecodedHttpResponse<unknown>> = {
  key: QK
  fn?: QueryFunction<Either<E, P>>
  config?: Omit<UseQueryOptions<P['payload'], E['error']>, 'queryKey' | 'queryFn'>
}

export const useQueryEither = <E extends ErrorType, QK extends QueryKey, P extends DecodedHttpResponse<unknown>>({
  key,
  fn,
  config,
}: UseQueryEitherOptions<QK, E, P>) => {
  const queryFn = useCallback(
    async (context: QueryFunctionContext<QK>) => {
      if (!fn) {
        return true
      }

      const response = await fn(context)

      if (isRight(response)) {
        return response.right.payload
      }

      throw response.left.error
    },
    [fn]
  )

  return useQuery<P['payload'], E['error']>(key, queryFn, {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    onError: e => {
      if (config?.onError) {
        config.onError(e)
      }

      return new Promise<void>(() => ({}))
    },
    ...((config && omit(config, 'onError')) || {}),
  })
}
